import { FC, useState } from 'react';
import { Modal, Stack, Text, Group, Button } from '@mantine/core';
import { Encounter } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';

interface CancelEncounterModalProps {
  opened: boolean;
  onClose: () => void;
  encounter: Encounter;
  onEncounterUpdated?: (encounter: Encounter) => void;
}

export const CancelEncounterModal: FC<CancelEncounterModalProps> = ({
  opened,
  onClose,
  encounter,
  onEncounterUpdated,
}) => {
  const { t } = useTranslation(['encounters']);
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    try {
      const updatedEncounter: Encounter = {
        ...encounter,
        status: 'cancelled',
        period: {
          ...encounter.period,
          end: new Date().toISOString(),
        },
      };

      const result = await medplum.updateResource(updatedEncounter);

      notifications.show({
        icon: <IconCircleCheck />,
        title: t('success', { ns: 'encounters' }),
        message: t('cancelled', { ns: 'encounters' }),
      });

      onEncounterUpdated?.(result);
      onClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: t('error', { ns: 'encounters' }),
        message:
          error instanceof Error
            ? error.message
            : t('cancelError', { ns: 'encounters' }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('cancel.title', { ns: 'encounters' })}
      size="md"
    >
      <Stack gap="md">
        <Text size="sm">{t('cancel.confirmation', { ns: 'encounters' })}</Text>

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            {t('buttons.back', { ns: 'encounters' })}
          </Button>
          <Button color="red" onClick={handleCancel} loading={loading}>
            {t('cancel.button', { ns: 'encounters' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
